import { FileOperation, JournalSummary, FileMetadata } from '../interfaces/python.interface';
import { PythonUtils } from '../../../helpers/python-utils.helper';

/**
 * PyodideFileJournal - tracks all file operations for safe cleanup
 *
 * This class maintains a journal of every file, directory, and package
 * operation performed in Pyodide, allowing for precise cleanup that
 * only removes user-created content while preserving system files.
 */
export class PyodideFileJournal {
  private readonly sitePackages = '/lib/python3.12/site-packages';
  private operations: FileOperation[] = [];
  private readonly mountPoints = new Map<string, 'MEMFS' | 'NODEFS'>();
  private readonly systemPackagesSnapshot = new Set<string>();
  private currentProjectId?: number;
  private readonly fileMetadata = new Map<string, FileMetadata>();

  // Package tracking optimization
  private readonly loadedPythonPackages = new Set<string>();

  // Import analysis caching
  private readonly analyzedFiles = new Map<string, string[]>();

  /**
   * Set current project ID for operation tagging
   */
  setProjectId(projectId: number): void {
    this.currentProjectId = projectId;
  }

  /**
   * Capture system packages on initialization
   */
  captureSystemState(python: any): void {
    const sitePackages = this.sitePackages;

    if (python.FS.analyzePath(sitePackages).exists) {
      try {
        const items = python.FS.readdir(sitePackages);
        items.forEach((item: string) => {
          if (item !== '.' && item !== '..') {
            this.systemPackagesSnapshot.add(item);
          }
        });
      } catch (error) {
        console.warn('Python journal: failed to capture system packages', error);
      }
    }
  }

  /**
   * Log mount operation
   */
  logMount(path: string, type: 'MEMFS' | 'NODEFS'): void {
    this.mountPoints.set(path, type);
    this.operations.push({
      type: 'mount',
      path,
      mountType: type,
      timestamp: Date.now(),
      projectId: this.currentProjectId
    });
  }

  /**
   * Log creation of a file with metadata for semantic tracking
   * @param path - Full path to the created file
   * @param source - Source of the file (e.g., 'user-file', 'cache', 'upload')
   * @param metadata - Optional metadata for semantic file tracking
   */
  logFileCreation(path: string, source: string = 'user', metadata?: Partial<FileMetadata>): void {
    // Normalize path for consistent lookup
    const normalizedPath = PythonUtils.normalizePath(path);

    // Don't journal system files
    if (this.isSystemFile(normalizedPath)) {
      return;
    }

    // Create full metadata with defaults
    const fullMetadata: FileMetadata = {
      origin: metadata?.origin || this.inferOrigin(source),
      fileType: metadata?.fileType || this.extractFileType(normalizedPath),
      timestamp: Date.now(),
      persistent: metadata?.persistent,
      creator: metadata?.creator,
      purpose: metadata?.purpose,  // No inference - caller provides if needed
      size: metadata?.size
    };

    // Store metadata with normalized path as key
    this.fileMetadata.set(normalizedPath, fullMetadata);

    // Store operation with metadata (using normalized path)
    this.operations.push({
      type: 'file',
      path: normalizedPath,
      timestamp: Date.now(),
      source,
      projectId: this.currentProjectId,
      metadata: fullMetadata
    });
  }

  /**
   * Check if a file should be preserved based on metadata
   * @param path - File path to check
   */
  shouldKeepFile(path: string): boolean {
    // Normalize path for consistent lookup
    const normalizedPath = PythonUtils.normalizePath(path);

    const metadata = this.fileMetadata.get(normalizedPath);
    if (!metadata) {
      // No metadata = not tracked = keep by default (safety-first)
      // Unknown files should be preserved to avoid accidental data loss
      return true;
    }

    // Keep files marked as persistent
    // This is the PRIMARY check - if file has persistent=true, keep it
    if (metadata.persistent) {
      return true;
    }

    // Default: don't keep (DELETE BY DEFAULT)
    // Files without persistent=true are temporary and should be removed
    return false;
  }

  /**
   * Get all file metadata for cleanup
   * Returns the file metadata map for efficient iteration
   */
  getFileMetadata(): Map<string, FileMetadata> {
    return this.fileMetadata;
  }

  /**
   * Check if file has metadata in journal
   * Used to determine if file should be preserved when journal is cleared
   */
  hasMetadata(path: string): boolean {
    const normalizedPath = PythonUtils.normalizePath(path);
    return this.fileMetadata.has(normalizedPath);
  }

  /**
   * Get files by origin
   * @param origin - Origin to filter by
   */
  getFilesByOrigin(origin: FileMetadata['origin']): string[] {
    const files: string[] = [];
    this.fileMetadata.forEach((metadata, path) => {
      if (metadata.origin === origin) {
        files.push(path);
      }
    });
    return files;
  }

  /**
   * Helper to infer origin from source string
   */
  private inferOrigin(source: string): FileMetadata['origin'] {
    if (source.includes('cache')) return 'cache';
    if (source.includes('user')) return 'user';
    if (source.includes('project')) return 'project';
    if (source.includes('step')) return 'step';
    if (source.includes('generated')) return 'generated';
    return 'user';
  }

  /**
   * Helper to extract file type from path
   */
  private extractFileType(path: string): string {
    const parts = path.split('.');
    return parts.length > 1 ? parts[parts.length - 1] : '';
  }

  /**
   * Log creation of a directory
   * PRIORITY 2 FIX: Also tracks in fileMetadata so cleanup preserves directories
   * @param path - Full path to the created directory
   */
  logDirectoryCreation(path: string): void {
    // Don't journal system directories
    if (this.isSystemFile(path)) {
      return;
    }

    const normalizedPath = PythonUtils.normalizePath(path);

    // Add to operations array (for history)
    this.operations.push({
      type: 'directory',
      path: normalizedPath,
      timestamp: Date.now(),
      projectId: this.currentProjectId
    });

    // CRITICAL FIX: Also add to fileMetadata so cleanup knows to keep it
    // Without this, directories get lost when journal is cleared
    // this.fileMetadata.set(normalizedPath, {
    //   origin: 'system',
    //   fileType: 'directory',
    //   timestamp: Date.now(),
    //   persistent: true,  // Directories are ALWAYS persistent
    //   purpose: 'temp',
    //   size: 0,
    //   creator: 'system'
    // });
  }

  /**
   * Log registration of a Python module
   * @param moduleName - Name of the registered module
   * @param filePath - Path to the module file
   */
  logModuleRegistration(moduleName: string, filePath: string): void {
    this.operations.push({
      type: 'module',
      path: filePath,
      data: { moduleName, projectId: this.currentProjectId },
      timestamp: Date.now()
    });
  }

  /**
   * Log installation of a package and its files
   * @param packageName - Name of the installed package
   * @param files - Array of file paths created by package installation
   */
  logPackageInstallation(packageName: string, files: string[]): void {
    files.forEach(file => {
      this.operations.push({
        type: 'package',
        path: file,
        timestamp: Date.now(),
        source: packageName
      });
    });
  }

  // ========================================================================
  // 🔥 PHASE 2.1: Refactoring - REMOVAL of filesystem management methods
  // PyodideFileJournal now ONLY logs operations, does not execute them
  // ========================================================================

  /**
   * Get operations for cleanup execution
   * (Does NOT perform cleanup itself)
   */
  getOperationsForCleanup(): FileOperation[] {
    return [...this.operations].reverse(); // Copy in reverse order for correct deletion
  }

  /**
   * Mark operations as processed after successful cleanup
   */
  markOperationsAsProcessed(): void {
    const _count = this.operations.length;
    this.operations.forEach(op => {
      (op as any).processed = true;
      (op as any).processedAt = Date.now();
    });
  }

  /**
   * Log Python package as loaded (optimization)
   * Avoids re-loading packages that are already in Pyodide
   */
  logPythonPackageLoaded(packageName: string): void {
    this.loadedPythonPackages.add(packageName);
  }

  /**
   * Check if Python package already loaded
   * Returns true if package is in Journal, avoiding Pyodide FS checks
   */
  isPackageLoaded(packageName: string): boolean {
    return this.loadedPythonPackages.has(packageName);
  }

  /**
   * Get all loaded packages
   */
  getLoadedPackages(): string[] {
    return Array.from(this.loadedPythonPackages);
  }

  /**
   * Log analyzed file with its dependencies
   * Avoids re-analyzing same file content (saves ~1.5s per file)
   */
  logFileAnalysis(filePath: string, dependencies: string[]): void {
    this.analyzedFiles.set(filePath, dependencies);
  }

  /**
   * Get cached analysis for file
   * Returns null if not analyzed yet
   */
  getFileAnalysis(filePath: string): string[] | null {
    const cached = this.analyzedFiles.get(filePath);
    return cached || null;
  }

  /**
   * Clear package tracking and analysis cache
   * Called on project/workflow switch
   */
  clearPackages(): void {
    const _packageCount = this.loadedPythonPackages.size;
    const _analysisCount = this.analyzedFiles.size;

    this.loadedPythonPackages.clear();
    this.analyzedFiles.clear();
  }

  /**
   * Clear journal after successful cleanup
   */
  clear(): void {
    const _operationCount = this.operations.length;
    this.operations = [];
    this.mountPoints.clear();
    this.fileMetadata.clear();

    // Also clear package tracking
    this.clearPackages();
  }

  /**
   * Get the number of operations in the journal
   * @returns number of tracked operations
   */
  getJournalSize(): number {
    return this.operations.length;
  }

  /**
   * Get a summary of journal contents
   * @returns JournalSummary object with operation statistics
   */
  getJournalSummary(): JournalSummary {
    const files = this.operations.filter(op => op.type === 'file').length;
    const directories = this.operations.filter(op => op.type === 'directory').length;
    const packages = this.operations.filter(op => op.type === 'package').length;
    const sources = [...new Set(this.operations.filter(op => op.source).map(op => op.source!))];

    return {
      totalOperations: this.operations.length,
      files,
      directories,
      packages,
      sources
    };
  }

  /**
   * Get all operations for debugging purposes
   * @returns Array of all file operations
   */
  getAllOperations(): FileOperation[] {
    return [...this.operations]; // Return copy to prevent external modification
  }

  /**
   * Check if a specific path is tracked in the journal
   * @param path - Path to check
   * @returns boolean indicating if path is tracked
   */
  isPathTracked(path: string): boolean {
    return this.operations.some(op => op.path === path);
  }

  /**
   * Get operations by source
   * @param source - Source to filter by
   * @returns Array of operations from the specified source
   */
  getOperationsBySource(source: string): FileOperation[] {
    return this.operations.filter(op => op.source === source);
  }

  /**
   * Remove specific operation from journal (useful for error handling)
   * @param path - Path of operation to remove
   */
  removeOperation(path: string): void {
    const normalizedPath = PythonUtils.normalizePath(path);
    const _initialLength = this.operations.length;
    this.operations = this.operations.filter(op => op.path !== normalizedPath);

    // Also remove from fileMetadata Map
    this.fileMetadata.delete(normalizedPath);
  }

  /**
   * NEW METHOD: Get list of registered modules
   * @returns Array of registered module names for cleanup
   */
  getRegisteredModules(): string[] {
    return this.operations
      .filter(op => op.type === 'module')
      .map(op => op.data?.moduleName as string)
      .filter(Boolean);
  }

  /**
   * NEW METHOD: Clear project journal
   * Used when switching projects
   */
  clearProject(): void {
    const _moduleCount = this.getRegisteredModules().length;
    this.operations = [];
    this.fileMetadata.clear();
    this.currentProjectId = undefined;  // Reset project ID on clear
  }

  /**
   * Get all installed packages with their files
   * @returns Map<packageName, filePaths[]>
   */
  getInstalledPackages(): Map<string, string[]> {
    const packages = new Map<string, string[]>();

    this.operations
      .filter(op => op.type === 'package')
      .forEach(op => {
        const packageName = op.source || 'unknown';
        if (!packages.has(packageName)) {
          packages.set(packageName, []);
        }
        packages.get(packageName)!.push(op.path);
      });

    return packages;
  }

  /**
   * Clear all package installation records
   */
  clearPackageInstallations(): void {
    this.operations = this.operations.filter(op => op.type !== 'package');
  }

  /**
   * Check if file is a system file
   * @private
   */
  private isSystemFile(path: string): boolean {
    // All files in site-packages are system files (including user-installed packages)
    if (path.startsWith(this.sitePackages) || path.includes('/site-packages/')) {
      return true;
    }

    // Other system directories
    return path.startsWith('/usr/') ||
      path.startsWith('/bin/') ||
      path.startsWith('/sbin/') ||
      path.startsWith('/lib/') ||
      path.startsWith('/dev/') ||
      path.startsWith('/proc/');
  }

  /**
   * Get file metadata by path
   * Returns undefined if file not tracked
   */
  getFileMetadataByPath(path: string): FileMetadata | undefined {
    const normalizedPath = PythonUtils.normalizePath(path);
    return this.fileMetadata.get(normalizedPath);
  }

  /**
   * Update existing file metadata (for file modifications)
   * Used when file content changes - updates hash, size, timestamp
   */
  updateFileMetadata(path: string, updates: Partial<FileMetadata>): void {
    const normalizedPath = PythonUtils.normalizePath(path);
    const existing = this.fileMetadata.get(normalizedPath);

    if (!existing) {
      // File not in journal - log as new with provided metadata
      this.logFileCreation(normalizedPath, 'user', updates);
      return;
    }

    // Merge updates with existing metadata
    this.fileMetadata.set(normalizedPath, {
      ...existing,
      ...updates,
      timestamp: Date.now()  // Always update timestamp on modification
    });
  }

  /**
   * Remove file metadata from journal
   * Called when file is deleted
   */
  removeFile(path: string): void {
    const normalizedPath = PythonUtils.normalizePath(path);
    this.fileMetadata.delete(normalizedPath);

    // Also remove from operations array to keep journal clean
    this.operations = this.operations.filter(op => op.path !== normalizedPath);
  }
}
